import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase, type AppRole } from "@/lib/supabase";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  roles: AppRole[];
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  useEffect(() => {
    // CRITICAL: set listener BEFORE getSession
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      try {
        setSession(newSession);
        
        if (newSession?.user) {
          if (event === 'SIGNED_IN') {
            await supabase
              .from('profiles')
              .update({ active_session_id: newSession.access_token.slice(-20) })
              .eq('id', newSession.user.id);
          }

          // Atualiza o store global para isolamento do IndexedDB
          try {
            const { useFiscalStore } = await import("@/store/fiscal-store");
            useFiscalStore.getState().setUserId(newSession.user.id);
          } catch (e) {
            console.error("Erro ao sincronizar userId com store:", e);
          }

          loadRoles(newSession.user.id).finally(() => setLoading(false));
        } else {
          // Limpa o userId do store no logout
          try {
            const { useFiscalStore } = await import("@/store/fiscal-store");
            useFiscalStore.getState().setUserId(null);
          } catch (e) {}
          setRoles([]);
          setLoading(false);
        }
      } catch (err) {
        console.error("Erro no AuthStateChange:", err);
        setLoading(false);
      }
    });

    // Check para Sessão Única (Anti-Sharing)
    const checkSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession?.user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('active_session_id')
          .eq('id', currentSession.user.id)
          .single();

        if (profile?.active_session_id && profile.active_session_id !== currentSession.access_token.slice(-20)) {
          console.warn("Sessão encerrada: Outro dispositivo detectado.");
          await supabase.auth.signOut();
          setSession(null);
          setRoles([]);
          window.location.href = "/login?error=session_conflict";
        }
      } catch (e) {
        // Ignora erro de rede no check invisível
      }
    };

    // Heartbeat: Atualiza a sessão a cada 5 minutos para evitar expiração por inatividade do Supabase
    const heartbeat = setInterval(async () => {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (s) setSession(s);
    }, 5 * 60 * 1000);

    // Inatividade: Logoff automático após 30 minutos sem interação
    let inactivityTimer: NodeJS.Timeout;
    const resetTimer = () => {
      setLastActivity(Date.now());
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(async () => {
        console.warn("Sessão encerrada por inatividade (30 minutos).");
        await signOut();
        window.location.href = "/login?error=timeout";
      }, 30 * 60 * 1000);
    };

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    // Safety Timeout: Garante que o loading NUNCA fique preso para sempre
    let safetyTimer: NodeJS.Timeout;
    if (loading) {
      safetyTimer = setTimeout(() => {
        setLoading(prev => {
          if (prev) {
            console.warn("[auth] Safety timeout atingido (10s). Forçando liberação da interface.");
            return false;
          }
          return prev;
        });
      }, 10000);
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user) {
        loadRoles(data.session.user.id).finally(() => {
          clearTimeout(safetyTimer);
          setLoading(false);
        });
      } else {
        clearTimeout(safetyTimer);
        setLoading(false);
      }
    }).catch((err) => {
      console.error("Erro no getSession:", err);
      clearTimeout(safetyTimer);
      setLoading(false);
    });

    return () => {
      clearTimeout(safetyTimer);
      sub.subscription.unsubscribe();
      clearInterval(heartbeat);
      clearTimeout(inactivityTimer);
      activityEvents.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, []);

  async function loadRoles(userId: string) {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (!error && data) setRoles(data.map((r) => r.role as AppRole));
  }

  async function signOut() {
    try {
      // Limpa dados em memória
      const { useFiscalStore } = await import("@/store/fiscal-store");
      useFiscalStore.getState().purgeAllData();
    } catch {}

    // Limpa estado local imediatamente
    setSession(null);
    setRoles([]);
    setLoading(false);

    // Tenta deslogar no servidor (fire and forget)
    supabase.auth.signOut().catch(() => {});
  }

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    roles,
    isAdmin: roles.includes("admin"),
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
